import React from 'react';

import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isUiSchemaForStepTypeV2,
  stepIsConfigEditorSupported,
} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {PipelineStep, pipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {getOutputStepDataRawDatas, inputStepDatasAreComplete} from 'pipelines/StepDataUtils';
import Float from 'ui-library/floating-ui/Float';

import {DEFAULT_FLOAT_PROPS} from './PopoverConstants';
import {RawDataPopoverContents} from './RawDataPopover';
import TableCreationPopoverContents, {
  TABLE_CREATION_POPOVER_SUPPORTED_STEP_TYPES,
} from './TableCreationPopoverContents';
import TransformPopoverContents from './TransformPopoverContents';

function getStepPopoverContentsRenderFn(step: PipelineStep): (() => JSX.Element) | null {
  const {config, stepType, statusCode} = step;

  // Defensively, don't try to render content for steps with incomplete input
  if (!inputStepDatasAreComplete(step) || config == null) {
    return null;
  }

  if (stepIsConfigEditorSupported(step) && !isUiSchemaForStepTypeV2(step.stepType)) {
    return () => <TransformPopoverContents step={step} />;
  }

  if (TABLE_CREATION_POPOVER_SUPPORTED_STEP_TYPES.has(stepType)) {
    return () => <TableCreationPopoverContents step={step} />;
  }

  if (statusCode !== PipelineStepStatusCode.SUCCEEDED) {
    return null;
  }

  if (
    pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step) ||
    pipelineStepIsStepType(PipelineStepType.REGRESSION, 0, step)
  ) {
    const rawDatas = getOutputStepDataRawDatas(step, 'Output chart');
    return rawDatas.length > 0 ? () => <RawDataPopoverContents rawData={rawDatas[0]} /> : null;
  }

  if (pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, step)) {
    const rawDatas = getOutputStepDataRawDatas(step, 'Output dataset');
    return rawDatas.length > 0 ? () => <RawDataPopoverContents rawData={rawDatas[0]} /> : null;
  }

  return null;
}

interface StepPopoverProps {
  step: PipelineStep;
  children: JSX.Element;
  disabled?: boolean;
}

/**
 * Show a popover with details about a step.
 *
 * Popover contents are different for each step type, and not all step types have content.
 *
 * Popover contents should be read-only.
 */
export default function StepPopover({step, children, disabled}: StepPopoverProps): JSX.Element {
  const renderContentsFn = getStepPopoverContentsRenderFn(step);

  if (disabled || renderContentsFn == null) {
    return <div className="u-cursor-default">{children}</div>;
  }

  return (
    <Float {...DEFAULT_FLOAT_PROPS} renderContent={renderContentsFn}>
      <div className="u-pointer">{children}</div>
    </Float>
  );
}
